import { cashRegisterService } from '@/pages/cash-register/services/cashRegisterService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Helper function to get auth token
const getAuthToken = () => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token;
};

// Helper function to make API requests
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

interface Sale {
  id: string;
  folio: string;
  total: number;
  metodoPago: string;
  estado: string;
  createdAt: string;
  items?: Array<{
    productId: string;
    cantidad: number;
    precioUnitario: number;
    descuento: number;
  }>;
}

interface CashRegisterTransaction {
  id: string;
  time: string;
  date?: string;
  type: 'ingreso' | 'egreso';
  description: string;
  amount: number;
  shift: string;
  shiftName: string;
  sessionId?: string; // Optional sessionId to track which cash register session this belongs to
}

interface CashRegisterReportData {
  transactions: CashRegisterTransaction[];
  cashCount: {
    bills: Record<string, number>;
    coins: Record<string, number>;
    totalCounted: number;
  } | null;
  summary: {
    openingAmount: number;
    totalSales: number;
    totalExpenses: number;
    netMovement: number;
    expectedBalance: number;
    cashSales: number;
    cardSales: number;
    yapePlinSales: number;
    appSales: number;
  };
  isOpened: boolean;
}

export const reportsService = {
  /**
   * Fetch sales data for a specific location and date range
   */
  async getSalesData(locationId: string, dateFrom: string, dateTo: string): Promise<Sale[]> {
    try {
      // Try to get sales from database first
      const data = await apiRequest(`/locations/${locationId}/sales-db?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      return data.data || [];
    } catch (error) {
      console.warn('Database sales endpoint failed, falling back to JSON endpoint:', error);
      // Fallback to JSON endpoint
      try {
        const data = await apiRequest(`/locations/${locationId}/sales?dateFrom=${dateFrom}&dateTo=${dateTo}`);
        return data.data || [];
      } catch (fallbackError) {
        console.error('Both sales endpoints failed:', fallbackError);
        return [];
      }
    }
  },

  /**
   * Get cash register report data for a specific location and date
   */
  async getCashRegisterReportData(locationId: string, date: string, endDate?: string): Promise<CashRegisterReportData> {
    try {
      // Check if there are any cash register records for this date (opened or closed)
      const endDateParam = endDate && endDate !== date ? `?endDate=${endDate}` : '';
      const allShiftsResponse = await apiRequest(`/cash-register/${locationId}/${date}/all-shifts${endDateParam}`);
      const hasAnyRegisters = allShiftsResponse && allShiftsResponse.shifts && allShiftsResponse.shifts.length > 0;
      
      if (!hasAnyRegisters) {
        return {
          transactions: [],
          cashCount: null,
          summary: {
            openingAmount: 0,
            totalSales: 0,
            totalExpenses: 0,
            netMovement: 0,
            expectedBalance: 0,
            cashSales: 0,
            cardSales: 0,
            yapePlinSales: 0,
            appSales: 0,
          },
          isOpened: false,
        };
      }

      // Get daily summary and cash count (best-effort, may fail for date ranges)
      let summary: any = { openingAmount: 0, totalSales: 0, totalExpenses: 0, expenses: [] };
      let cashCount: any = null;
      try {
        summary = await cashRegisterService.getDailySummary(locationId, date);
      } catch (e) {
        console.warn('Could not get daily summary:', e);
      }
      try {
        cashCount = await cashRegisterService.getCashCount(locationId, date);
      } catch (e) {
        console.warn('Could not get cash count:', e);
      }

      // Get sales data directly from the detailed sales API
      const effectiveEndDate = endDate || date;
      let salesApiResponse;
      let totalSalesFromApi = 0;

      try {
        salesApiResponse = await apiRequest(`/reports/detailed-sales/${locationId}?startDate=${date}&endDate=${effectiveEndDate}`);
        totalSalesFromApi = salesApiResponse?.data?.summary?.totalSales || 0;
      } catch (error) {
        console.warn('Could not get detailed sales data:', error);
        salesApiResponse = null;
      }
      
      // Transform data into transactions format with cash register sessions
      const transactions: CashRegisterTransaction[] = [];
      
      // Track payment methods by session
      const sessionPaymentTotals: Record<string, {
        efectivo: number;
        tarjeta: number;
        yapePlin: number;
        aplicativo: number;
      }> = {};
      
      // Process each cash register session directly from the response
      if (allShiftsResponse && allShiftsResponse.shifts && Array.isArray(allShiftsResponse.shifts)) {
        // Process sessions sequentially to get their specific sales
        for (const [index, session] of allShiftsResponse.shifts.entries()) {
          const sessionId = `${session.shift}-${session.openedAt}`;
          const openTime = new Date(session.openedAt);
          const closeTime = session.closedAt ? new Date(session.closedAt) : null;
          
          // Get sales specific to this cash register session
          let sessionSales = [];
          try {
            const salesResponse = await apiRequest(`/cash-register/session/${session.id}/sales`);
            sessionSales = salesResponse.data || [];
          } catch (error) {
            console.warn(`Could not get sales for cash register session ${session.id}:`, error);
            sessionSales = [];
          }
          
          // Determine shift based on opening time
          const hours = openTime.getHours();
          let shift = 'morning';
          let shiftName = 'Mañana';
          
          if (hours >= 14 && hours < 22) {
            shift = 'afternoon';
            shiftName = 'Tarde';
          } else if (hours >= 22 || hours < 6) {
            shift = 'night';  
            shiftName = 'Noche';
          }
          
          const sessionNumber = index + 1;
          
          // Add opening balance for this cash register session
          transactions.push({
            id: `opening-balance-${sessionId}`,
            time: openTime.toLocaleTimeString('es-PE', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }),
            date: session.date,
            type: 'ingreso',
            description: `Apertura de Caja #${sessionNumber} - ${shiftName}`,
            amount: parseFloat(session.openingAmount) || 0,
            shift,
            shiftName,
            sessionId,
          });
          
          // Add sales specific to this session
          sessionSales.forEach((sale) => {
            const saleTime = new Date(sale.createdAt);
            
            // Initialize session payment totals if not exists
            if (!sessionPaymentTotals[sessionId]) {
              sessionPaymentTotals[sessionId] = {
                efectivo: 0,
                tarjeta: 0,
                yapePlin: 0,
                aplicativo: 0
              };
            }
            
            // Get payment method description and update totals
            let paymentMethodDesc = '';
            switch(sale.metodoPago) {
              case 'EFECTIVO':
                paymentMethodDesc = 'Efectivo';
                sessionPaymentTotals[sessionId].efectivo += sale.total;
                break;
              case 'TARJETA_CREDITO':
              case 'TARJETA_DEBITO':
              case 'TARJETA':
                paymentMethodDesc = 'Tarjeta';
                sessionPaymentTotals[sessionId].tarjeta += sale.total;
                break;
              case 'YAPE':
              case 'PLIN':
              case 'YAPE_PLIN':
                paymentMethodDesc = 'Yape/Plin';
                sessionPaymentTotals[sessionId].yapePlin += sale.total;
                break;
              case 'APLICATIVO':
                paymentMethodDesc = 'Aplicativo';
                sessionPaymentTotals[sessionId].aplicativo += sale.total;
                break;
              default:
                paymentMethodDesc = sale.metodoPago;
                sessionPaymentTotals[sessionId].efectivo += sale.total;
            }
            
            transactions.push({
              id: sale.id,
              time: saleTime.toLocaleTimeString('es-PE', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }),
              date: session.date,
              type: 'ingreso',
              description: `Venta ${sale.folio} (${paymentMethodDesc})`,
              amount: sale.total,
              shift,
              shiftName,
              sessionId,
            });
          });
          
          // Add expenses/incomes for this session
          if (session.expenses && Array.isArray(session.expenses)) {
            session.expenses.forEach((expense: any) => {
              const expTime = new Date(expense.createdAt);
              const isIncome = expense.type === 'income';
              const categoryLabels: Record<string, string> = {
                'ENVIO_DINERO': 'Envío de Dinero',
                'REEMBOLSO': 'Reembolso',
                'BONIFICACION': 'Bonificación',
                'OTROS_GASTOS': 'Otros Gastos',
                'GAS': 'Gas',
                'maintenance': 'Mantenimiento',
                'marketing': 'Marketing',
                'refund': 'Reembolso',
                'cashback': 'Cashback',
                'income': 'Ingreso',
                'expense': 'Gasto',
              };
              const categoryLabel = categoryLabels[expense.category] || expense.category;
              const desc = expense.description
                ? `${categoryLabel}: ${expense.description}`
                : categoryLabel;

              transactions.push({
                id: `expense-${expense.id}`,
                time: expTime.toLocaleTimeString('es-PE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }),
                date: session.date,
                type: isIncome ? 'ingreso' : 'egreso',
                description: desc,
                amount: isIncome ? expense.amount : -expense.amount,
                shift,
                shiftName,
                sessionId,
              });
            });
          }

          // Add closing balance if the register is closed
          if (closeTime) {
            // Use closingBalance if available, otherwise fallback to previous logic
            const finalAmount = parseFloat(session.closingBalance) || parseFloat(session.totalCounted) || parseFloat(session.expectedCash) || parseFloat(session.openingAmount) || 0;
            transactions.push({
              id: `closing-balance-${sessionId}`,
              time: closeTime.toLocaleTimeString('es-PE', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }),
              date: session.date,
              type: 'ingreso',
              description: `Cierre de Caja #${sessionNumber} - ${shiftName}`,
              amount: finalAmount,
              shift,
              shiftName,
              sessionId,
            });

            // Add difference if exists
            const difference = parseFloat(session.difference) || 0;
            if (Math.abs(difference) > 0.01) {
              transactions.push({
                id: `difference-${sessionId}`,
                time: closeTime.toLocaleTimeString('es-PE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }),
                date: session.date,
                type: difference > 0 ? 'ingreso' : 'egreso',
                description: `Diferencia Caja #${sessionNumber} - ${difference > 0 ? 'Sobrante' : 'Faltante'}`,
                amount: Math.abs(difference),
                shift,
                shiftName,
                sessionId,
              });
            }
          } else {
            // If cash register is still open, add a note
            transactions.push({
              id: `open-status-${sessionId}`,
              time: '23:59',
              date: session.date,
              type: 'ingreso',
              description: `Caja #${sessionNumber} - Aún Abierta`,
              amount: 0,
              shift,
              shiftName,
              sessionId,
            });
          }
        }
      }
      
      // If no sessions data from new endpoint but we have basic data, create fallback
      if ((!allShiftsResponse || !allShiftsResponse.shifts || allShiftsResponse.shifts.length === 0) && summary.openingAmount > 0) {
        transactions.push({
          id: 'opening-balance-default',
          time: '06:00',
          type: 'ingreso',
          description: 'Saldo Inicial',
          amount: summary.openingAmount || 0,
          shift: 'morning',
          shiftName: 'Mañana',
        });
      }
      
      // Sales and expenses are now processed within each cash register session above
      // This ensures that each transaction belongs only to the session it was created in

      // Add payment method summaries for each session with sales
      // This now correctly reflects only the sales that occurred during each specific session
      Object.entries(sessionPaymentTotals).forEach(([sessionId, totals]) => {
        const hasAnySales = totals.efectivo > 0 || totals.tarjeta > 0 || totals.yapePlin > 0 || totals.aplicativo > 0;
        
        if (hasAnySales) {
          // Find the session to get shift info
          let shift = 'morning';
          let shiftName = 'Mañana';
          
          if (allShiftsResponse && allShiftsResponse.shifts && Array.isArray(allShiftsResponse.shifts)) {
            const session = allShiftsResponse.shifts.find(s => `${s.shift}-${s.openedAt}` === sessionId);
            if (session) {
              const sessionOpenTime = new Date(session.openedAt);
              const hours = sessionOpenTime.getHours();
              
              if (hours >= 14 && hours < 22) {
                shift = 'afternoon';
                shiftName = 'Tarde';
              } else if (hours >= 22 || hours < 6) {
                shift = 'night';  
                shiftName = 'Noche';
              }
            }
          }
          
          // Add separator transaction
          transactions.push({
            id: `payment-summary-separator-${sessionId}`,
            time: '---',
            type: 'ingreso',
            description: `--- Resumen Caja ${sessionId.split('-')[0]} ---`,
            amount: 0,
            shift,
            shiftName,
          });
          
          // Add payment method totals
          if (totals.efectivo > 0) {
            transactions.push({
              id: `payment-efectivo-${sessionId}`,
              time: '---',
              type: 'ingreso',
              description: '💰 Total Efectivo (Esta Caja)',
              amount: totals.efectivo,
              shift,
              shiftName,
            });
          }
          
          if (totals.tarjeta > 0) {
            transactions.push({
              id: `payment-tarjeta-${sessionId}`,
              time: '---',
              type: 'ingreso',
              description: '💳 Total Tarjeta (Esta Caja)',
              amount: totals.tarjeta,
              shift,
              shiftName,
            });
          }
          
          if (totals.yapePlin > 0) {
            transactions.push({
              id: `payment-yapeplin-${sessionId}`,
              time: '---',
              type: 'ingreso',
              description: '📱 Total Yape/Plin (Esta Caja)',
              amount: totals.yapePlin,
              shift,
              shiftName,
            });
          }
          
          if (totals.aplicativo > 0) {
            transactions.push({
              id: `payment-aplicativo-${sessionId}`,
              time: '---',
              type: 'ingreso',
              description: '🚀 Total Aplicativo (Esta Caja)',
              amount: totals.aplicativo,
              shift,
              shiftName,
            });
          }
        }
      });
      
      // Sort transactions by date then time
      transactions.sort((a, b) => {
        // First sort by date
        if (a.date && b.date && a.date !== b.date) {
          return a.date.localeCompare(b.date);
        }
        const timeA = a.time.split(':').map(Number);
        const timeB = b.time.split(':').map(Number);
        return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
      });
      
      return {
        transactions,
        cashCount,
        summary: {
          openingAmount: summary.openingAmount || 0,
          totalSales: totalSalesFromApi,
          totalExpenses: summary.totalExpenses || 0,
          netMovement: summary.netMovement || 0,
          expectedBalance: summary.expectedBalance || 0,
          cashSales: summary.cashSales || 0,
          cardSales: summary.cardSales || 0,
          yapePlinSales: summary.yapePlinSales || 0,
          appSales: summary.appSales || 0,
        },
        isOpened: hasAnyRegisters,
      };
      
    } catch (error) {
      console.error('Error fetching cash register report data:', error);
      throw error;
    }
  },
  
  /**
   * Get cash register history for multiple dates
   */
  async getCashRegisterHistory(locationId: string): Promise<any[]> {
    return cashRegisterService.getCashRegisterHistory(locationId);
  },

  /**
   * Get top products report data
   */
  async getTopProductsReport(
    locationId: string, 
    filters: {
      startDate?: string;
      endDate?: string;
      shift?: string;
      category?: string;
      limit?: number;
      sortBy?: string;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.shift && filters.shift !== 'all') queryParams.append('shift', filters.shift);
      if (filters.category && filters.category !== 'all') queryParams.append('category', filters.category);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);

      const queryString = queryParams.toString();
      const url = `/reports/top-products/${locationId}${queryString ? `?${queryString}` : ''}`;
      
      return await apiRequest(url);
    } catch (error) {
      console.error('Error fetching top products report:', error);
      throw error;
    }
  },

  /**
   * Get payment methods report data
   */
  async getPaymentMethodsReport(
    locationId: string,
    filters: {
      startDate?: string;
      endDate?: string;
      shift?: string;
      includeDetail?: boolean;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();

      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.shift && filters.shift !== 'all') queryParams.append('shift', filters.shift);
      if (filters.includeDetail) queryParams.append('includeDetail', 'true');

      const queryString = queryParams.toString();
      const url = `/reports/payment-methods/${locationId}${queryString ? `?${queryString}` : ''}`;

      return await apiRequest(url);
    } catch (error) {
      console.error('Error fetching payment methods report:', error);
      throw error;
    }
  },

  /**
   * Get stock report data
   */
  async getStockReport(
    locationId: string,
    filters: {
      category?: string;
      status?: string;
      ingredientId?: string;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.category && filters.category !== 'all') queryParams.append('category', filters.category);
      if (filters.status && filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters.ingredientId && filters.ingredientId !== 'all') queryParams.append('ingredientId', filters.ingredientId);

      const queryString = queryParams.toString();
      const url = `/reports/stock/${locationId}${queryString ? `?${queryString}` : ''}`;
      const response = await apiRequest(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching stock report:', error);
      throw error;
    }
  },

  /**
   * Get consumption report data
   */
  async getConsumptionReport(
    locationId: string,
    filters: {
      startDate?: string;
      endDate?: string;
      shift?: string;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.shift && filters.shift !== 'all') queryParams.append('shift', filters.shift);

      const queryString = queryParams.toString();
      const url = `/reports/consumption/${locationId}${queryString ? `?${queryString}` : ''}`;
      const response = await apiRequest(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching consumption report:', error);
      throw error;
    }
  },

  /**
   * Get general report (cash register by location and shift)
   */
  async getGeneralReport(
    locationId: string,
    filters: {
      startDate?: string;
      endDate?: string;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const queryString = queryParams.toString();
      const url = `/reports/general/${locationId}${queryString ? `?${queryString}` : ''}`;
      return await apiRequest(url);
    } catch (error) {
      console.error('Error fetching general report:', error);
      throw error;
    }
  },

  /**
   * Get differences report (transfers and purchase orders)
   */
  async getDifferencesReport(
    locationId: string,
    filters: {
      startDate?: string;
      endDate?: string;
      preparedBy?: string;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.preparedBy) queryParams.append('preparedBy', filters.preparedBy);

      const queryString = queryParams.toString();
      const url = `/reports/differences/${locationId}${queryString ? `?${queryString}` : ''}`;
      return await apiRequest(url);
    } catch (error) {
      console.error('Error fetching differences report:', error);
      throw error;
    }
  },

  /**
   * Get inventory errors report
   */
  async getInventoryErrorsReport(
    locationId: string,
    filters: {
      startDate?: string;
      endDate?: string;
      cashierId?: string;
      errorType?: string;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.cashierId) queryParams.append('cashierId', filters.cashierId);
      if (filters.errorType) queryParams.append('errorType', filters.errorType);

      const queryString = queryParams.toString();
      const url = `/reports/inventory-errors/${locationId}${queryString ? `?${queryString}` : ''}`;
      return await apiRequest(url);
    } catch (error) {
      console.error('Error fetching inventory errors report:', error);
      throw error;
    }
  },

  /**
   * Get production efficiency report
   */
  async getProductionEfficiencyReport(
    locationId: string,
    filters: {
      startDate?: string;
      endDate?: string;
    }
  ) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const queryString = queryParams.toString();
      const url = `/reports/production-efficiency/${locationId}${queryString ? `?${queryString}` : ''}`;
      return await apiRequest(url);
    } catch (error) {
      console.error('Error fetching production efficiency report:', error);
      throw error;
    }
  },
};